<script>
import { ref, onMounted, watch } from 'vue';

/**
 * 応援pt トースト UI (ユーザー側 / キャスト側 両対応)。
 *
 * 仕様: _docs/event/130-spec.md §5.6 LV up トースト表示
 *
 * Props:
 *   - show: bool トースト表示フラグ
 *   - mode: 'user' | 'cast'  送信側か受信側か (文言切替)
 *   - level: number LV
 *   - cheerPt: number 送られた応援pt
 *   - characterName: string 対象キャラ名 (user用)
 *   - userName: string ユーザー名 (cast用)
 *   - bonuses: array LV報酬リスト (LV5/8/15/20/30到達時)
 *   - durationMs: number 表示時間 (デフォルト 4000ms, 報酬付与時は 6000ms 推奨)
 *
 * Events:
 *   - hidden トースト非表示遷移時
 */
export default {
    name: 'CheerPointToast',
    props: {
        show: { type: Boolean, default: false },
        mode: { type: String, default: 'user' },
        level: { type: Number, default: 0 },
        cheerPt: { type: Number, default: 0 },
        characterName: { type: String, default: '' },
        userName: { type: String, default: '' },
        bonuses: { type: Array, default: () => [] },
        durationMs: { type: Number, default: 4000 },
    },
    emits: ['hidden'],
    setup(props, { emit }) {
        const visible = ref(false);
        let timerId = null;

        function showToast() {
            visible.value = true;
            clearTimeout(timerId);
            const dur = props.bonuses.length > 0 ? Math.max(props.durationMs, 6000) : props.durationMs;
            timerId = setTimeout(() => {
                visible.value = false;
                emit('hidden');
            }, dur);
        }

        watch(() => props.show, (newVal) => {
            if (newVal) showToast();
        });

        onMounted(() => {
            if (props.show) showToast();
        });

        return { visible };
    },
};
</script>

<template>
    <transition name="cheer-toast">
        <div v-if="visible" class="cheer-toast" :class="mode">
            <div class="cheer-toast-title">
                🎀 Lv.{{ level }} 達成!
            </div>
            <div class="cheer-toast-body">
                <template v-if="mode === 'user'">
                    {{ characterName }}に <strong>{{ cheerPt }}</strong> 応援ポイント送りました!
                </template>
                <template v-else>
                    {{ userName }}さんから <strong>{{ cheerPt }}</strong> 応援ポイント受け取りました!
                </template>
            </div>
            <div v-if="bonuses.length > 0" class="cheer-toast-bonuses">
                <div v-for="(b, idx) in bonuses" :key="idx" class="cheer-toast-bonus">
                    ✨ {{ b.reward_name || b.message }}
                </div>
            </div>
        </div>
    </transition>
</template>

<style scoped>
.cheer-toast {
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    min-width: 280px;
    max-width: 90vw;
    padding: 16px 24px;
    background: linear-gradient(135deg, #ff8ab5 0%, #ff5577 100%);
    color: #fff;
    border-radius: 12px;
    box-shadow: 0 6px 20px rgba(255, 85, 119, 0.4);
    text-align: center;
    font-family: inherit;
}
.cheer-toast.cast {
    background: linear-gradient(135deg, #b8a9d9 0%, #8c7cb0 100%);
    box-shadow: 0 6px 20px rgba(140, 124, 176, 0.4);
}
.cheer-toast-title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 8px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.cheer-toast-body {
    font-size: 14px;
    line-height: 1.5;
}
.cheer-toast-body strong {
    font-size: 18px;
    font-weight: 900;
}
.cheer-toast-bonuses {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.4);
    font-size: 13px;
}
.cheer-toast-bonus {
    margin: 4px 0;
}

/* transition */
.cheer-toast-enter-active,
.cheer-toast-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.cheer-toast-enter-from {
    opacity: 0;
    transform: translateX(-50%) scale(0.5);
}
.cheer-toast-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
}
</style>
